/**
 * Speaking-assessment API client.
 *
 * One endpoint, one shape. Replaces the pre-v2 separate
 * `pronunciationService` + `vocabularyPronunciationService` calls.
 * Both course shadowing and vocabulary book practice send their
 * audio + reference text here and get back transcript + 4-axis
 * scores + per-word breakdown + prosody contour + Loopy coach.
 */
import apiClient from './apiClient';

export type AsrProviderId = 'groq-whisper' | 'deepgram';
export type DiffOp = 'match' | 'substitute' | 'delete' | 'insert';

export interface DiffEntry {
    ref?: string;
    hyp?: string;
    op: DiffOp;
}

export interface DiffSummary {
    totalRefWords: number;
    matches: number;
    substitutions: number;
    deletions: number;
    insertions: number;
    wer: number;
    entries: DiffEntry[];
}

export interface AssessmentScores {
    overall: number;
    accuracy: number;
    fluency: number;
    completeness: number;
    prosody: number;
}

export interface AssessmentWord {
    word: string;
    accuracyScore: number;
    errorType: 'None' | 'Mispronunciation' | 'Omission' | 'Insertion';
    voicedRatio?: number;
    suggestion?: string;
}

export interface ProsodyContour {
    f0Frames: Array<number | null>;
    frameTimes: number[];
    silent: boolean[];
}

export interface ProsodySummary {
    pitchRangeSt: number;
    pitchVarianceSt: number;
    voicedRatio: number;
    score: number;
    contour: ProsodyContour;
    peak?: { wordIndex?: number; word?: string; semitonesFromMedian: number };
    trough?: { wordIndex?: number; word?: string; semitonesFromMedian: number };
}

export interface CoachTip {
    issue: string;
    advice: string;
    refLink: string | null;
}

export interface CoachResult {
    encouragement: string;
    tips: CoachTip[];
}

export interface AssessmentIssue {
    category: string;
    severity: 'low' | 'medium' | 'high';
    evidence: {
        metric?: string;
        value?: number;
        word?: string;
        wordIndex?: number;
        heardAs?: string;
    };
}

export interface SpeakingAssessmentResponse {
    transcript: string;
    asrProvider: AsrProviderId;
    asrFallbackReason?: string;
    diff: DiffSummary;
    scores: AssessmentScores;
    words: AssessmentWord[];
    prosody: ProsodySummary;
    /** Now optional — server returns scores fast and the client
     *  fetches coach via the separate /speaking/coach endpoint so
     *  the score panel paints in ~2s instead of waiting for
     *  Gemini. The legacy single-call shape stays valid for
     *  backwards compat. */
    coach?: CoachResult;
    /** Detected issues passed back to /speaking/coach on the
     *  follow-up call — saves the server from re-deriving them
     *  from the diff/words. */
    issues: AssessmentIssue[];
    serverLatencyMs: number;
}

export interface AssessRequest {
    audioBase64: string;
    mimeType: string;
    referenceText: string;
    /** BCP-47 locale tag for coach output (e.g., 'zh-TW'). Defaults
     *  to user's saved preference / 'en' when omitted. */
    locale?: string;
}

export interface GenerateCoachRequest {
    referenceText: string;
    transcript?: string;
    locale?: string;
    /** Overall score the panel displays — drives Gemini tone
     *  calibration so 77/100 doesn't get celebratory copy. */
    overallScore?: number;
    prosody: {
        score: number;
        pitchRangeSt: number;
        pitchVarianceSt: number;
        voicedRatio: number;
    };
    issues: AssessmentIssue[];
    /** Per-word breakdown so the coach won't compliment a word it
     *  also has to flag as mispronounced. */
    words?: Array<{
        word: string;
        errorType: AssessmentWord['errorType'];
    }>;
}

export const speakingAssessmentService = {
    async assess(req: AssessRequest): Promise<SpeakingAssessmentResponse> {
        const res = await apiClient.post<SpeakingAssessmentResponse>(
            '/speaking/assess',
            req,
            { timeout: 90_000 },
        );
        return res.data;
    },

    async generateCoach(req: GenerateCoachRequest): Promise<CoachResult> {
        const res = await apiClient.post<CoachResult>(
            '/speaking/coach',
            req,
            { timeout: 60_000 },
        );
        return res.data;
    },
};
